import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Description
} from '@headlessui/react';
import { Button } from '@teachstonellc/component-library';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import I18n from 'i18n-js';
import i18n, { TranslateArg } from 'helpers/i18n';
import RouteHelpers from 'helpers/routes';
import ageLevelMessages from 'helpers/ageLevel/messages';
import { TokenRedemptionConfirmation } from 'actions/productKeys';
import messages from './messages';

type TokenRedemptionDialogProps = {
  confirmation: TokenRedemptionConfirmation | null;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const ageLevelLabels: Record<string, TranslateArg> = {
  infant: ageLevelMessages.infant,
  toddler: ageLevelMessages.toddler,
  'infant-toddler': ageLevelMessages.infantToddler,
  'pre-kindergarten': ageLevelMessages.preK,
  'pre-kindergarten-3rd': messages.tokenRedemption.preKThird,
  kindergarten: ageLevelMessages.k,
  'kindergarten-to-3rd': ageLevelMessages.k3,
  'upper-elementary': ageLevelMessages.upperElementary,
  secondary: ageLevelMessages.secondary,
  'age-neutral': messages.tokenRedemption.ageNeutral
};

function TokenRedemptionDialog({
  confirmation,
  isLoading,
  onConfirm,
  onCancel
}: TokenRedemptionDialogProps) {
  const cancelButton = useRef<HTMLButtonElement>(null);
  if (!confirmation) return null;

  const content = messages.tokenRedemption;
  const date = DateTime.fromISO(confirmation.previous_redemption.redeemed_at)
    .setLocale(I18n.locale)
    .toLocaleString(DateTime.DATE_MED);
  const template = confirmation.calibration_template;
  const ageLevelMessage = ageLevelLabels[template.age_level_group];
  const ageLevel = ageLevelMessage
    ? i18n.ft(ageLevelMessage)
    : template.age_level_group_name;
  const edition = i18n.ft(
    template.class_version === 'class_2008'
      ? content.firstEdition
      : content.secondEdition
  );

  return (
    <Dialog
      open
      initialFocus={cancelButton}
      onClose={() => !isLoading && onCancel()}
      className="relative z-50 font-sans"
    >
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="relative w-full max-w-[642px] max-h-[90vh] overflow-y-auto rounded-[24px] bg-white px-6 pb-8 pt-16 shadow-xl sm:px-10 text-center">
          <button
            type="button"
            aria-label={i18n.ft(content.close)}
            disabled={isLoading}
            onClick={onCancel}
            className="absolute right-5 top-5 p-1 text-black"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M4 4l16 16M20 4L4 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <DialogTitle className="m-0 text-2xl font-bold text-black">
            {i18n.ft(content.title)}
          </DialogTitle>
          <Description as="div" className="mt-8 text-lg text-text-dark">
            <p>
              {i18n.ft(content.previousActivation, { date })}{' '}
              {i18n.ft(content.findCalibrations)}{' '}
              <Link
                to={RouteHelpers.getPath('calibrations-view')}
                onClick={event => {
                  if (isLoading) event.preventDefault();
                  else onCancel();
                }}
                aria-disabled={isLoading}
                className="text-[#1A0733] underline"
              >
                {i18n.ft(content.calibrationsLink)}
              </Link>
              .
            </p>
            <p className="mt-8">
              {i18n.ft(content.anotherVideo, { edition, ageLevel })}
            </p>
          </Description>
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Button
              ref={cancelButton}
              variant="secondary"
              className="flex-1 !rounded-full"
              onClick={onCancel}
              disabled={isLoading}
            >
              {i18n.ft(messages.cancel)}
            </Button>
            <Button
              variant="primary"
              className="flex-1 !rounded-full !bg-[#1A0733]"
              onClick={onConfirm}
              disabled={isLoading}
              loading={isLoading}
            >
              {i18n.ft(content.activateAgain)}
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default TokenRedemptionDialog;
